import { CompactContactPreviewChip } from "@/components/ui/ContactPreviewChip/CompactContactPreviewChip";
import { MatterDetailsQuickEditSlideover } from "@/components/features/matter/information/MatterDetailsQuickEditSlideover";
import { MatterPersonnelEditor } from "@/components/features/matter/information/MatterPersonnelEditor";
import { getPersonnelUserStatus } from "@/components/features/matter/information/MatterPersonnelTeamMembership";
import MatterTagConnectionsSection from "@/components/features/matter/information/MatterTagConnectionsSection";
import { MatterStatusTransitionDropdown } from "@/components/features/matter/MatterStatusTransitionDropdown";
import { MatterPhaseBar } from "@/components/features/matter/overview/matter-phases/MatterPhaseBar";
import {
  ActionConfirmationModal,
  Badge,
  Button,
  InlineExpandableText,
} from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { UserPhotoRow } from "@/components/ui/UserPhotoRow/UserPhotoRow";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { retrieveContactQueryOptions } from "@/lib/data/react-query/contacts/contact";
import { getUsersOptions } from "@/lib/data/react-query";
import { useDeleteMatter } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMatterPinned,
  useDeleteMatterPinned,
} from "@/lib/data/react-query/matters/matter-pinned";
import { useDeleteMatterRelatedMatterConnection } from "@/lib/data/react-query/matters/matter-related-matter";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
  PAGE_PERMISSION_KEYS,
} from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { copyToClipboard } from "@/lib/utils/copyToClipboard";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import {
  formatAddress,
  formatLocalOrIntlPhoneNumber,
  formatUsersName,
} from "@/lib/utils/format";
import {
  getPrimaryItem,
  getResolvedPrimaryEmail,
  getResolvedPrimaryPhone,
} from "@/lib/utils/matterContactConnectionHelpers";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import {
  ChevronRight,
  CircleAlert,
  Copy,
  CopyCheck,
  Mail,
  MapPin,
  Mails,
  MessageSquareText,
  Pencil,
  Phone,
  Pin,
  Plus,
  TriangleAlert,
  X,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import {
  getActiveAssignees,
  getActiveMatterStatusPhase,
  getActiveTeams,
  getOverviewClientSummarySegments,
  getOverviewIncidentSummarySegments,
  getTopStats,
} from "../matter-overview-utils";
import { EarliestSolTopStat } from "./EarliestSolTopStat";
import { HeaderButton } from "./HeaderButton";
import { HeroImageCard } from "./HeroImageCard";
import { OverviewSectionSidebar } from "./OverviewSectionSidebar";
import { InsuranceLiabilityTopStat } from "./InsuranceLiabilityTopStat";
import { TotalDamagesTopStat } from "./TotalDamagesTopStat";

function CopyableOverviewDetail({
  copyValue,
  icon: Icon,
  label,
  showSeparator,
  showLabel = false,
  value,
}: {
  copyValue?: string;
  icon?: LucideIcon;
  label: string;
  showSeparator: boolean;
  showLabel?: boolean;
  value: string;
}) {
  return (
    <span className="whitespace-nowrap">
      {showSeparator ? (
        <span aria-hidden="true" className="text-border whitespace-pre">
          {" | "}
        </span>
      ) : null}
      <TooltipWrapper message={`Copy ${label.toLowerCase()}`}>
        <button
          type="button"
          className="hover:text-foreground focus-visible:text-foreground focus-visible:ring-ring focus-visible:ring-offset-background inline cursor-copy rounded-sm text-left transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none"
          aria-label={`Copy ${label.toLowerCase()}`}
          onClick={() => {
            void copyToClipboard(copyValue ?? value, label);
          }}
        >
          {Icon ? (
            <Icon
              aria-hidden="true"
              className="mr-1.5 inline h-3.5 w-3.5 align-text-bottom"
            />
          ) : null}
          <span>
            {showLabel ? `${label}: ` : null}
            {value}
          </span>
        </button>
      </TooltipWrapper>
    </span>
  );
}

export function OverviewContent({ matter }: { matter: Matter }) {
  const { hasActionPermission, hasPagePermission, currentUser } = useUser();
  const canViewPhases = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailPhases,
  );
  const {
    openAddRelatedMatterModal,
    openCommunicationModal,
    openFileModal,
    openSendMatterEmailModal,
  } = useGlobalModal();
  const navigate = useNavigate();
  const deleteMatter = useDeleteMatter();
  const createPinnedMatter = useCreateMatterPinned();
  const deletePinnedMatter = useDeleteMatterPinned();
  const deleteRelatedMatterConnection =
    useDeleteMatterRelatedMatterConnection();
  const activeTeams = getActiveTeams(matter);
  const activeAssignees = getActiveAssignees(matter);
  const primaryAssignee = activeAssignees.find(
    (assignee) => assignee.primaryContact,
  );
  const { data: usersData } = useQuery(
    getUsersOptions(activeAssignees.length > 0),
  );
  const disabledUserIds = new Set(
    (usersData?.data ?? [])
      .filter((user) => user.enabled === false)
      .map((user) => user.id),
  );
  const primaryAssigneeStatus = primaryAssignee
    ? getPersonnelUserStatus(
        matter,
        [],
        disabledUserIds,
        primaryAssignee.user.id,
      )
    : undefined;
  const primaryChipLabel = primaryAssignee
    ? `Primary: ${formatUsersName(primaryAssignee.user) ?? "Unknown user"}${primaryAssigneeStatus ? ` · ${primaryAssigneeStatus}` : ""}`
    : null;
  const primaryRoles = primaryAssignee
    ? [
        ...new Set(
          activeAssignees
            .filter((assignee) => assignee.user.id === primaryAssignee.user.id)
            .map((assignee) => assignee.role.label),
        ),
      ].join(", ")
    : "";
  const hasInactiveAssignees = activeAssignees.some((assignee) =>
    getPersonnelUserStatus(matter, [], disabledUserIds, assignee.user.id),
  );
  const claimTypes = matter.claimTypes ?? [];
  const linkedCases = matter.relatedMatterConnections;
  const clientConnection = getMatterContactConnections(matter).find(
    (connection) => connection.label === "Client",
  );
  const { data: clientData } = useQuery(
    retrieveContactQueryOptions(clientConnection?.contactId ?? ""),
  );
  const clientSummarySegments = getOverviewClientSummarySegments(
    matter,
    clientData?.data,
  );
  const incidentSummarySegments = getOverviewIncidentSummarySegments(matter);
  const incidentDescription = matter.incidentDescription?.trim();
  const criticalInfo = matter.criticalInfo?.trim();
  const injuryDescription = matter.clientInjuryDescription?.trim();
  const clientContact = clientData?.data;
  const showClientMinorBadge =
    clientContact?.type === "individual" && clientContact.individualMinor;
  const showClientDeceasedBadge =
    clientContact?.type === "individual" && clientContact.individualDeceased;
  const clientPhone =
    getPrimaryItem(clientContact?.phones ?? [], "isPrimary") ??
    getResolvedPrimaryPhone(matter, clientConnection);
  const clientPhoneNumber = clientPhone?.number;
  const formattedClientPhone = formatLocalOrIntlPhoneNumber(clientPhone);
  const clientEmail =
    getPrimaryItem(clientContact?.emails ?? [], "isPrimary") ??
    getResolvedPrimaryEmail(matter, clientConnection);
  const clientEmailAddress = clientEmail?.address;
  const clientAddress =
    getPrimaryItem(clientContact?.addresses ?? [], "isPrimary") ??
    getPrimaryItem(clientConnection?.contact.addresses ?? [], "isPrimary");
  const formattedClientAddress = clientAddress
    ? formatAddress({
        street1: clientAddress.street1,
        street2: clientAddress.street2,
        city: clientAddress.city,
        state: clientAddress.state,
        zipCode: clientAddress.zipCode,
        country:
          clientAddress.country === "United States"
            ? undefined
            : clientAddress.country,
      })
    : "";
  const topStats = getTopStats(matter);
  const [isQuickEditOpen, setIsQuickEditOpen] = useState(false);
  const [isPersonnelOpen, setIsPersonnelOpen] = useState(false);
  const [isInfoCardExpanded, setIsInfoCardExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [
    relatedMatterConnectionIdToDelete,
    setRelatedMatterConnectionIdToDelete,
  ] = useState<string | null>(null);
  const [copyLink, setCopyLink] = useState(false);
  const matterLinkRef = useRef<HTMLAnchorElement | null>(null);
  const canUpdateMatter = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const canChangeMatterStatus = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatusPhase,
    ACTION_PERMISSION_VALUES.create,
  );
  const canDeleteMatter = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canOpenQuickEdit = canUpdateMatter || canDeleteMatter;
  const canViewPersonnel = Boolean(true);
  const canViewContactCommunications = Boolean(true);
  const hasMatterTagViewPermission = Boolean(true);
  const hasMatterTagCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTagConnection,
    ACTION_PERMISSION_VALUES.create,
  );
  const hasMatterTagDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTagConnection,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canViewRelatedMatters = Boolean(true);
  const canCreateRelatedMatters = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterRelatedMatterConnection,
    ACTION_PERMISSION_VALUES.create,
  );
  const canDeleteRelatedMatters = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterRelatedMatterConnection,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canTextClient = Boolean(
    canViewContactCommunications && clientConnection?.contactId,
  );
  const canCallClient = Boolean(clientPhoneNumber);
  const canEmailClient = Boolean(clientEmailAddress);
  const matterPath = `/matters/${matter.id}`;
  const matterHref =
    typeof window === "undefined"
      ? matterPath
      : `${window.location.origin}${matterPath}`;
  const activeStatusPhase = getActiveMatterStatusPhase(matter);
  const timeInStatusDays = activeStatusPhase?.dateTimeStart
    ? Math.max(dayjs().diff(dayjs(activeStatusPhase.dateTimeStart), "day"), 0)
    : null;
  const currentPinnedMatter = useMemo(
    () => matter.pinnedMatters.find((pin) => pin.userId === currentUser.id),
    [matter.pinnedMatters, currentUser.id],
  );
  const isPinnedByCurrentUser = Boolean(currentPinnedMatter);
  const isPinnedMutationPending =
    createPinnedMatter.isPending || deletePinnedMatter.isPending;

  const handleDeleteMatter = () => {
    deleteMatter.mutate({ id: matter.id });
    void navigate({ to: "/matters" });
  };
  const handleCopyLink = async () => {
    if (!matterLinkRef.current || typeof window === "undefined") {
      return;
    }
    setCopyLink(true);
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(matterLinkRef.current);
    selection?.removeAllRanges();
    selection?.addRange(range);
    const finish = () => {
      selection?.removeAllRanges();
      setTimeout(() => {
        setCopyLink(false);
      }, 2000);
    };
    const linkHtml = matterLinkRef.current.outerHTML;
    const linkText = matterLinkRef.current.textContent ?? matter.title;
    if (typeof ClipboardItem !== "undefined") {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([linkHtml], { type: "text/html" }),
            "text/plain": new Blob([linkText], { type: "text/plain" }),
          }),
        ]);
        finish();
        return;
      } catch {
        // fallback to writeText if rich copy fails
      }
    }
    await navigator.clipboard.writeText(linkText);
    finish();
  };
  const handleOpenText = () => {
    if (!clientConnection?.contactId) {
      return;
    }
    openCommunicationModal({
      contactId: clientConnection.contactId,
      matterId: matter.id,
      setSelectedContactId: () => {},
    });
  };
  const handleCallClient = () => {
    if (!clientPhoneNumber || typeof window === "undefined") {
      return;
    }
    const sanitizedPhoneNumber = clientPhoneNumber.replace(/[^0-9+]/g, "");
    if (!sanitizedPhoneNumber) {
      return;
    }
    window.open(`tel:${sanitizedPhoneNumber}`, "_self");
  };
  const handleEmailClient = () => {
    if (!clientEmailAddress) {
      return;
    }

    openSendMatterEmailModal({
      matterId: matter.id,
      contactId: clientConnection?.contactId,
      toEmails: [clientEmailAddress],
      templateData: {
        firm_name: currentUser.firm.name,
      },
      title: "Send email",
      description: "Compose and send an email for this matter.",
    });
  };

  return (
    <div className="space-y-6 pb-5">
      <div className="bg-background rounded-[28px] border">
        <div className="p-6 pb-5">
          <div className="flex flex-col gap-3.5">
            {criticalInfo ? (
              <aside
                aria-label="Critical matter information"
                className="flex items-center gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-yellow-950 dark:border-yellow-500/40 dark:bg-yellow-500/15 dark:text-yellow-100"
              >
                <TriangleAlert
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-yellow-700 dark:text-yellow-300"
                />
                <InlineExpandableText
                  text={criticalInfo}
                  maxLines={1}
                  className="min-w-0 text-sm leading-5 whitespace-pre-wrap"
                />
              </aside>
            ) : null}
            <div className="flex flex-col gap-4 xl:flex-row">
              <div className="shrink-0">
                <HeroImageCard
                  matter={matter}
                  onAddPhoto={() =>
                    openFileModal({
                      matterId: matter.id,
                      currentFolderId: "",
                      isFolder: false,
                      editing: false,
                      isCriticalImage: true,
                      autoOpenUpload: true,
                    })
                  }
                />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
                  <div className="min-w-64 flex-1">
                    <div className="flex items-center gap-1.5">
                      <a
                        ref={matterLinkRef}
                        href={matterHref}
                        onClick={(event) => {
                          event.preventDefault();
                        }}
                        className="text-foreground truncate text-[18px] leading-none font-semibold sm:text-[20px]"
                      >
                        {matter.title}
                      </a>
                      <TooltipWrapper
                        message={
                          isPinnedByCurrentUser ? "Unpin matter" : "Pin matter"
                        }
                      >
                        <span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={cn(
                              "h-6 w-6 shrink-0",
                              isPinnedByCurrentUser &&
                                "bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 dark:bg-blue-500/15 dark:text-blue-300 dark:hover:bg-blue-500/25 dark:hover:text-blue-200",
                            )}
                            aria-label={
                              isPinnedByCurrentUser
                                ? "Unpin matter"
                                : "Pin matter"
                            }
                            disabled={isPinnedMutationPending}
                            onClick={async () => {
                              if (
                                isPinnedByCurrentUser &&
                                currentPinnedMatter
                              ) {
                                await deletePinnedMatter.mutateAsync({
                                  pinnedMatterId: currentPinnedMatter.id,
                                });
                                return;
                              }
                              await createPinnedMatter.mutateAsync({
                                matterId: matter.id,
                              });
                            }}
                          >
                            <Pin
                              className={cn(
                                "h-3.5 w-3.5",
                                isPinnedByCurrentUser
                                  ? "fill-current"
                                  : "text-muted-foreground",
                              )}
                            />
                          </Button>
                        </span>
                      </TooltipWrapper>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground h-6 w-6 shrink-0"
                        onClick={() => {
                          void handleCopyLink();
                        }}
                        aria-label="Copy matter link"
                      >
                        {copyLink ? (
                          <CopyCheck className="h-3.5 w-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </Button>
                      <TooltipWrapper message="Copy matter email">
                        <span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground h-6 w-6 shrink-0"
                            aria-label="Copy matter email"
                            disabled={!matter.emailAddress}
                            onClick={() => {
                              void copyToClipboard(
                                matter.emailAddress ?? "",
                                "Matter email",
                              );
                            }}
                          >
                            <Mails className="h-3.5 w-3.5" />
                          </Button>
                        </span>
                      </TooltipWrapper>
                      {canOpenQuickEdit ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground h-6 w-6 shrink-0"
                          onClick={() => {
                            setIsQuickEditOpen(true);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      ) : null}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {matter.incidentType?.label ? (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
                          {matter.incidentType.label}
                        </span>
                      ) : null}
                      {showClientMinorBadge ? (
                        <Badge
                          variant="amber"
                          className="h-5 rounded-full px-2 py-0 text-[11px] font-medium"
                        >
                          Minor
                        </Badge>
                      ) : null}
                      {showClientDeceasedBadge ? (
                        <Badge
                          variant="red"
                          className="h-5 rounded-full px-2 py-0 text-[11px] font-medium"
                        >
                          Deceased
                        </Badge>
                      ) : null}
                      <MatterStatusTransitionDropdown
                        matter={matter}
                        canTransitionMatterStatus={canChangeMatterStatus}
                        buttonClassName="bg-emerald-100 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100/80 dark:bg-emerald-500/15 dark:text-emerald-300 dark:hover:bg-emerald-500/25"
                        readOnlyClassName="bg-emerald-100 text-[11px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                      />
                      {timeInStatusDays != null ||
                      topStats.daysOnDesk != null ? (
                        <span className="text-muted-foreground text-xs">
                          {[
                            timeInStatusDays != null
                              ? `${timeInStatusDays} ${timeInStatusDays === 1 ? "day" : "days"} in status`
                              : null,
                            topStats.daysOnDesk != null
                              ? `${topStats.daysOnDesk} ${topStats.daysOnDesk === 1 ? "day" : "days"} on desk`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" | ")}
                        </span>
                      ) : null}
                    </div>
                    {claimTypes.length > 0 ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {claimTypes.map((claimType) => (
                          <Badge
                            key={claimType.id}
                            variant="outline"
                            className="text-[11px]"
                          >
                            {claimType.label}
                          </Badge>
                        ))}
                      </div>
                    ) : null}
                    {hasMatterTagViewPermission ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <MatterTagConnectionsSection
                          tagConnections={matter.tagConnections}
                          matterId={matter.id}
                          hasCreatePermission={hasMatterTagCreatePermission}
                          hasDeletePermission={hasMatterTagDeletePermission}
                          inline
                        />
                      </div>
                    ) : null}
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      <HeaderButton
                        icon={<MessageSquareText className="mr-1 h-4 w-4" />}
                        label="Text"
                        onClick={handleOpenText}
                        disabled={!canTextClient}
                      />
                      <HeaderButton
                        icon={<Phone className="mr-1 h-4 w-4" />}
                        label="Call"
                        onClick={handleCallClient}
                        disabled={!canCallClient}
                      />
                      <HeaderButton
                        icon={<Mail className="mr-1 h-4 w-4" />}
                        label="Email"
                        onClick={handleEmailClient}
                        disabled={!canEmailClient}
                      />
                    </div>
                  </div>

                  <div className="mt-1 flex max-w-full min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-2">
                    {primaryChipLabel ? (
                      <TooltipWrapper
                        message={primaryRoles}
                        contentClassName="border border-border bg-popover text-popover-foreground shadow-md"
                      >
                        <span
                          className="inline-flex max-w-full"
                          tabIndex={0}
                          aria-label={`${primaryChipLabel}. Matter roles: ${primaryRoles}`}
                        >
                          <Badge
                            variant="blue"
                            className="max-w-full truncate px-3 py-1 text-[10px]"
                          >
                            {primaryChipLabel}
                          </Badge>
                        </span>
                      </TooltipWrapper>
                    ) : null}
                    <div className="flex max-w-full min-w-0 flex-wrap justify-end gap-1.5">
                      {activeTeams.map((team) => (
                        <Badge
                          key={team.id}
                          variant="secondary"
                          className="max-w-48 truncate text-[10px]"
                        >
                          {team.label}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      {activeAssignees.length > 0 ? (
                        <UserPhotoRow
                          users={[]}
                          assignees={activeAssignees}
                          statusFor={(userId) =>
                            getPersonnelUserStatus(
                              matter,
                              [],
                              disabledUserIds,
                              userId,
                            )
                          }
                          showTooltips
                        />
                      ) : null}
                      {hasInactiveAssignees ? (
                        <TooltipWrapper message="Disabled or deleted users are assigned to this matter.">
                          <button
                            type="button"
                            className="rounded-full text-amber-600 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none dark:text-amber-400"
                            aria-label="Disabled or deleted users are assigned to this matter"
                          >
                            <CircleAlert
                              className="h-5 w-5"
                              aria-hidden="true"
                            />
                          </button>
                        </TooltipWrapper>
                      ) : null}
                      {canViewPersonnel ? (
                        <Button
                          type="button"
                          variant={
                            activeAssignees.length === 0 ? "outline" : "ghost"
                          }
                          size={activeAssignees.length === 0 ? "sm" : "icon"}
                          data-testid="manage-personnel-button"
                          className={
                            activeAssignees.length === 0
                              ? "h-8 gap-1.5"
                              : "text-muted-foreground h-8 w-8 rounded-full border border-dashed"
                          }
                          onClick={() => {
                            setIsPersonnelOpen(true);
                          }}
                          aria-label={
                            activeAssignees.length === 0
                              ? activeTeams.length === 0
                                ? "Add teams"
                                : "Add people"
                              : "Manage personnel"
                          }
                        >
                          {activeAssignees.length === 0 ? (
                            <>
                              <Plus className="h-4 w-4" aria-hidden="true" />
                              {activeTeams.length === 0
                                ? "Add teams"
                                : "Add people"}
                            </>
                          ) : (
                            <Pencil className="h-4 w-4" />
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>

                {clientSummarySegments.length > 0 ? (
                  <p className="text-muted-foreground mt-3.5 text-sm leading-6">
                    {clientSummarySegments.map((segment, index) => (
                      <CopyableOverviewDetail
                        key={`${segment.label}-${String(index)}`}
                        label={segment.label}
                        showLabel
                        showSeparator={index > 0}
                        value={segment.value}
                      />
                    ))}
                  </p>
                ) : null}
                {formattedClientPhone ||
                clientEmailAddress ||
                formattedClientAddress ? (
                  <p
                    className={cn(
                      "text-muted-foreground text-sm leading-6",
                      clientSummarySegments.length > 0 ? "mt-1" : "mt-3.5",
                    )}
                  >
                    {formattedClientPhone && clientPhoneNumber ? (
                      <CopyableOverviewDetail
                        copyValue={clientPhoneNumber}
                        icon={Phone}
                        label="Phone number"
                        showSeparator={false}
                        value={formattedClientPhone}
                      />
                    ) : null}
                    {clientEmailAddress ? (
                      <CopyableOverviewDetail
                        icon={Mail}
                        label="Email address"
                        showSeparator={Boolean(formattedClientPhone)}
                        value={clientEmailAddress}
                      />
                    ) : null}
                    {formattedClientAddress ? (
                      <CopyableOverviewDetail
                        icon={MapPin}
                        label="Address"
                        showSeparator={
                          Boolean(formattedClientPhone) ||
                          Boolean(clientEmailAddress)
                        }
                        value={formattedClientAddress}
                      />
                    ) : null}
                  </p>
                ) : null}
                {incidentSummarySegments.length > 0 ? (
                  <p className="text-muted-foreground mt-1 text-sm leading-6">
                    {incidentSummarySegments.map((segment, index) => (
                      <CopyableOverviewDetail
                        key={`${segment.label}-${String(index)}`}
                        label={segment.label}
                        showLabel
                        showSeparator={index > 0}
                        value={segment.value}
                      />
                    ))}
                  </p>
                ) : null}
                {incidentDescription ? (
                  <div className="mt-3.5">
                    <p className="text-muted-foreground text-[10px] font-semibold">
                      Incident description
                    </p>
                    <div className="mt-1">
                      <InlineExpandableText
                        text={incidentDescription}
                        maxLines={3}
                        className="text-foreground leading-relaxed"
                      />
                    </div>
                  </div>
                ) : null}
                {(!matter.incidentType ||
                  matter.incidentType.injuriesModuleAccess) &&
                injuryDescription ? (
                  <div className="mt-3.5">
                    <p className="text-muted-foreground text-[10px] font-semibold">
                      Injuries
                    </p>
                    <div className="mt-1">
                      <InlineExpandableText
                        text={injuryDescription}
                        maxLines={3}
                        className="text-foreground leading-relaxed"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="overflow-hidden rounded-[18px] border">
              <div className="bg-card grid grid-cols-3 xl:grid-cols-5">
                <EarliestSolTopStat
                  statuteDate={topStats.statuteDate}
                  statuteDaysRemaining={topStats.statuteDaysRemaining}
                  statuteConfirmed={topStats.statuteConfirmed}
                  statuteId={topStats.statuteId}
                  statutes={matter.statutesOfLimitations}
                  isExpanded={isInfoCardExpanded}
                  onExpandedChange={setIsInfoCardExpanded}
                />
                <InsuranceLiabilityTopStat
                  className="md:col-span-2 xl:col-span-3"
                  matter={matter}
                  isExpanded={isInfoCardExpanded}
                  onExpandedChange={setIsInfoCardExpanded}
                />
                <TotalDamagesTopStat
                  className="xl:col-span-1"
                  matter={matter}
                  isExpanded={isInfoCardExpanded}
                  onExpandedChange={setIsInfoCardExpanded}
                />
              </div>
            </div>

            <div className="mt-3.5 flex flex-wrap gap-x-8 gap-y-4 empty:hidden">
              {(["Referral", "Referee"] as const).map((role) => {
                const connections = getMatterContactConnections(matter).filter(
                  (connection) => connection.label === role,
                );
                if (connections.length === 0) return null;
                return (
                  <div key={role}>
                    <p className="text-muted-foreground text-[10px] font-semibold">
                      {role === "Referral" ? "Referred by" : "Referred to"}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {connections.map((connection) => (
                        <CompactContactPreviewChip
                          key={connection.id}
                          contact={connection}
                          matter={matter}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {canViewRelatedMatters ? (
              <div className="mt-3.5">
                <div className="flex items-center gap-2">
                  <p className="text-muted-foreground text-[10px] font-semibold">
                    Related matters
                  </p>
                  {canCreateRelatedMatters ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground h-6 w-6 rounded-full border border-dashed"
                      onClick={() => {
                        openAddRelatedMatterModal({
                          matterId: matter.id,
                        });
                      }}
                      aria-label="Add related matter"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  ) : null}
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  {linkedCases.length > 0 ? (
                    linkedCases.map((linkedCase) => (
                      <div
                        key={linkedCase.id}
                        className="hover:bg-muted/40 flex max-w-full items-center rounded-full border text-sm font-medium transition-colors"
                      >
                        <Link
                          to="/matters/$matterId"
                          params={{
                            matterId: linkedCase.relatedMatterId,
                          }}
                          className="flex min-w-0 items-center gap-2 py-1.5 pl-3"
                        >
                          <span className="truncate">
                            {linkedCase.relatedMatterTitle ?? linkedCase.title}
                          </span>
                          <ChevronRight className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                        </Link>
                        {canDeleteRelatedMatters ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive mr-1 h-6 w-6 shrink-0 rounded-full"
                            aria-label={`Remove related matter ${linkedCase.relatedMatterTitle ?? linkedCase.title}`}
                            onClick={() => {
                              setRelatedMatterConnectionIdToDelete(
                                linkedCase.id,
                              );
                            }}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      No related matters.
                    </p>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-start gap-6 2xl:flex-row">
        {canViewPhases ? (
          <div className="w-full min-w-0 flex-1">
            <MatterPhaseBar matterId={matter.id} matter={matter} />
          </div>
        ) : null}
        <div
          className={cn("w-full shrink-0", canViewPhases && "2xl:w-[320px]")}
        >
          <OverviewSectionSidebar matter={matter} />
        </div>
      </div>

      {canViewPersonnel ? (
        <MatterPersonnelEditor
          matter={matter}
          open={isPersonnelOpen}
          onOpenChange={setIsPersonnelOpen}
        />
      ) : null}

      {canOpenQuickEdit ? (
        <MatterDetailsQuickEditSlideover
          matter={matter}
          open={isQuickEditOpen}
          setOpen={setIsQuickEditOpen}
          canUpdate={canUpdateMatter}
          canChangeStatus={canChangeMatterStatus}
          canDelete={canDeleteMatter}
          presentation="modal"
          onRequestDelete={() => {
            setIsDeleting(true);
          }}
        />
      ) : null}

      <ActionConfirmationModal
        open={isDeleting}
        setOpen={setIsDeleting}
        title="Are you sure you want to delete this matter?"
        afterActionFn={handleDeleteMatter}
      />
      <ActionConfirmationModal
        open={Boolean(relatedMatterConnectionIdToDelete)}
        setOpen={(open) => {
          if (!open) setRelatedMatterConnectionIdToDelete(null);
        }}
        title="Remove this related matter?"
        description="This removes the relationship between the two matters. Neither matter will be deleted."
        buttonText="Remove"
        afterActionFn={async () => {
          if (!relatedMatterConnectionIdToDelete || !canDeleteRelatedMatters) {
            return;
          }
          await deleteRelatedMatterConnection.mutateAsync({
            id: relatedMatterConnectionIdToDelete,
          });
          setRelatedMatterConnectionIdToDelete(null);
        }}
      />
    </div>
  );
}
